import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, ArrowRight } from "lucide-react";
import { startCheckout } from "../../lib/checkout";
import { useFilms } from "../../context/FilmsContext";
import { useAuth } from "../../context/AuthContext";

const PLAN = { key: "all_access_monthly", price: "$2", unit: "/ month", note: "Billed monthly · Cancel anytime" };

export const Pricing = () => {
  const { user, login } = useAuth();
  const { films } = useFilms();
  const [loading, setLoading] = useState(false);
  const plan = PLAN;
  const PERKS = [
    `The full ${films.length}-title archive — horror, sci-fi & comedy classics`,
    "4K-scanned restorations, ad-free and uninterrupted",
    "Unlimited downloads for your own midnight screenings",
    "Fresh restorations added every month",
    "Cancel anytime — it's two dollars, not a hostage situation",
  ];

  const subscribe = async () => {
    if (!user) {
      toast.info("Sign in with Google to subscribe.");
      login();
      return;
    }
    setLoading(true);
    try {
      startCheckout(user.email, user.user_id);
    } catch {
      toast.error("Something went wrong starting checkout.");
      setLoading(false);
    }
  };

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="relative py-28 md:py-44 px-5 md:px-10 bg-black overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute -top-40 left-1/2 -translate-x-1/2 h-[520px] w-[520px] rounded-full bg-blood/20 blur-[140px]"
      />
      <div className="relative max-w-[1600px] mx-auto grid md:grid-cols-2 gap-12 md:gap-20 items-center">
        <div>
          <span className="font-mono text-[11px] tracking-[0.4em] uppercase text-blood">
            (One Plan. All Access.)
          </span>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.19, 1, 0.22, 1] }}
            className="mt-5 font-serif text-6xl md:text-8xl leading-[0.85] tracking-tighter text-bone"
          >
            Two dollars
            <br />
            <span className="italic text-blood">a month.</span>
          </motion.h2>
          <p className="mt-7 text-base md:text-lg text-muted-foreground max-w-md leading-relaxed">
            Cheaper than a coffee, darker than midnight. Every restored horror
            and sci-fi classic, streaming and downloadable — for the price of
            loose change.
          </p>
          <ul className="mt-9 space-y-4">
            {PERKS.map((p) => (
              <li key={p} className="flex items-start gap-3">
                <Check className="h-4 w-4 text-blood mt-1 shrink-0" />
                <span className="text-sm md:text-base text-bone/85 leading-relaxed">
                  {p}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Plan card */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: [0.19, 1, 0.22, 1] }}
          data-testid="plan-card"
          className="relative border border-white/15 bg-gradient-to-b from-white/[0.04] to-transparent p-8 md:p-12"
        >
          <div className="flex items-center justify-between">
            <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-blood border border-blood/40 px-2 py-1">
              All Access
            </span>
            <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
              Cancel anytime
            </span>
          </div>

          <div className="mt-8 flex items-end gap-2">
            <span
              data-testid="plan-price"
              className="font-serif text-7xl md:text-8xl leading-none tracking-tighter text-bone"
            >
              {plan.price}
            </span>
            <span className="font-mono text-sm text-muted-foreground mb-3">
              {plan.unit}
            </span>
          </div>
          <p className="mt-3 font-mono text-[11px] tracking-[0.2em] uppercase text-muted-foreground">
            {plan.note}
          </p>

          <div className="mt-9 space-y-4">
            {user ? (
              <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground border-b border-white/15 pb-3">
                Billing to {user.email}
              </p>
            ) : (
              <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground border-b border-white/15 pb-3">
                Sign in with Google to subscribe
              </p>
            )}
            <button
              data-testid="subscribe-btn"
              onClick={subscribe}
              disabled={loading}
              className="group w-full inline-flex items-center justify-center gap-3 bg-blood text-white px-8 py-4 font-mono text-[11px] tracking-[0.25em] uppercase disabled:opacity-60 transition-transform duration-300 hover:-translate-y-0.5"
            >
              {loading
                ? "Opening checkout…"
                : user
                ? `Subscribe — ${plan.price}${plan.unit.replace(" ", "")}`
                : "Sign in to subscribe"}
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>

          <p className="mt-5 text-center font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
            Secure checkout via Stripe
          </p>
        </motion.div>
      </div>
    </section>
  );
};
